import { useState, useEffect, useCallback } from 'react';
import Storages from 'storages';
import StorageKeys from 'enum/storages.enum';
import EventBus from 'utils/eventBus';
import DeafultTimeZone from 'components/time/defaultTimeZone';
import { getCurrentDateTime, getWeatherDetail, parseWeatherDetails, resolveLocationTimeZone } from './utils';
import { isEqual } from 'utils/common/object';
import patientData from 'utils/patientData';
let previousWeatherData = { temp: '', humidity: '', condition: '' };
/**
 * Custom hook to fetch and manage header data (weather and dateTime)
 * Reusable across all components that need HeaderBar or similar data
 */
export const useHeaderData = () => {
  const [dateTime, setDateTime] = useState({ time: '', date: '' });
  const [weatherData, setWeatherData] = useState(previousWeatherData);
  const [customerData, setCustomerData] = useState({});
  const [roomData, setRoomData] = useState({});
  const [organizationLogoUrl, setOrganizationLogoUrl] = useState(null);

  const getOrganizationLogoUrl = useCallback(() => {
    const identity = Storages.MainStorage.getItem(StorageKeys.Keys.ORGANIZATION_IDENTITY);
    const raw = identity?.logo_url;
    const trimmed = typeof raw === 'string' ? raw.trim() : '';
    setOrganizationLogoUrl(trimmed ? trimmed : null);
  }, []);

  const getCustomerData = useCallback(() => {
    setCustomerData(patientData.getCustomerDisplayData());
  }, []);

  const getRoomData = useCallback(() => {
    const data = Storages.MainStorage.getItem(StorageKeys.Keys.ROOMDATA);
    setRoomData(data || {});
  }, []);

  const handleWeather = useCallback(async () => {
    const lang = Storages.MainStorage.getItem(StorageKeys.Keys.LANGUAGECODE)?.split('_')[0] || 'en';
    const location =
      Storages.MainStorage.getItem(StorageKeys.Keys.CURRENTLOCATION)?.city_name ||
      (DeafultTimeZone.new_york.split("/")[1] ?? "").replaceAll("_", " ");
    const weatherDetails = await getWeatherDetail(location, lang);
    if(weatherDetails==null) return
    const parsedDetails = parseWeatherDetails(weatherDetails);
    if (isEqual(previousWeatherData, parsedDetails)) {
      return;
    }
    previousWeatherData = parsedDetails;
    setWeatherData(parsedDetails);
  }, []);

  const getTime = useCallback(async () => {
    const mainLocation = resolveLocationTimeZone(
      Storages.MainStorage.getItem(StorageKeys.Keys.CURRENTLOCATION)
    );
    const mainDateTime = await getCurrentDateTime(mainLocation);

    setDateTime({
      time: mainDateTime.time,
      date: mainDateTime.date,
    });
  }, []);

  useEffect(() => {
    const onRefreshSub = EventBus.subscribe('refresh', () => {
      handleWeather();
      getCustomerData();
      getRoomData();
      getOrganizationLogoUrl();
    });

    const onRefreshCustomerDataSub = EventBus.subscribe('refreshCustomerData', () => {
      getCustomerData();
    });

    handleWeather();
    getCustomerData();
    getRoomData();
    getOrganizationLogoUrl();
    const timeInterval = setInterval(() => {
      getTime();
    }, 10000 * 6); // Update every 60 seconds

    getTime();

    return () => {
      clearInterval(timeInterval);
      onRefreshSub.unsubscribe();
      onRefreshCustomerDataSub.unsubscribe();
    };
  }, [handleWeather, getTime, getCustomerData, getRoomData, getOrganizationLogoUrl]);

  return { dateTime, weatherData, customerData, roomData, organizationLogoUrl };
};
